import { Haptics, NotificationType } from "@capacitor/haptics"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import type { IncomeDocument, IncomeErrors, TripLineItem } from '@/types/income'

import Button from "@/components/common/Button/Button"
import MotionText from "@/components/common/Motion/MotionText"
import MotionDiv from "@/components/common/Motion/MotionDiv"
import HeaderBack from "@/components/dashboard/Header/HeaderBack"
import DecimalInput from "@/components/common/Input/DecimalInput"
import DateInput from "@/components/common/Input/DateInput"

import { useKycStore } from "@/store/useKycStore"

import Input from "@/components/common/Input/Input"
import ProgressBar from "@/components/common/ProgressBar/ProgressBar"

import incomeAPI from "@/services/api/income"
import monthlyStatementsAPI from "@/services/api/monthlyStatements"
import { useBoltStore } from "@/store/useBoltStore"
import { useDashboardStore } from "@/store/useDashboardStore"
import { requestInAppReview } from "@/services/review"
import { usePageSetup } from "@/hooks/usePageSetup"

export const BoltIncomePage = () => {
  const { t } = useTranslation()
  usePageSetup(t('bolt.pageTitle.income'))
  /* Reusing KYC */
  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(3), [])

  const navigate = useNavigate()

  const balance = useBoltStore((state) => state.balanceAnalysis as IncomeDocument)
  const trips = useBoltStore((state) => state.tripAnalysis as TripLineItem[])
  const resetAnalysis = useBoltStore((state) => state.resetAnalysis)
  const dashboard = useDashboardStore((state) => state.dashboard)

  const tripTotal = trips?.reduce((sum, { amount }) => sum + amount, 0) || 0

  const balanceBonus = balance?.line_items?.find(
    item => item.category === 'bonus'
  )?.amount || 0

  const balanceTips = balance?.line_items?.find(
    item => item.category === 'tips'
  )?.amount || 0

  const balanceBonusOperation = balance?.line_items?.find(
    item => item.category === 'bonus'
  )?.description || ''

  const balanceTipsOperation = balance?.line_items?.find(
    item => item.category === 'tips'
  )?.description || ''

  const [trip, setTrip] = useState(tripTotal)
  const [bonus, setBonus] = useState(balanceBonus)
  const [tips, setTips] = useState(balanceTips)

  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<IncomeErrors>({})

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setIsLoading(true)

    /* TODO: This should be reworked to be a one endpoint
       If one fails we will create others which will cause bad data.
    */

    if (trips?.length > 0) {
      const [year, month] = trips[0].date.split('-').map(Number);
      const lastDayOfMonth = new Date(year, month, 0)

      const date = new Intl.DateTimeFormat(
        "lt-LT",
        { year: 'numeric', month: '2-digit', day: '2-digit' }
      ).format(lastDayOfMonth)

      const promises = [
        bonus > 0 && incomeAPI.create({
          income_category_code: "bonus",
          amount_cents: Math.round(bonus * 100),
          date: date,
          reference: [date, t('bolt.income.bonusReference')].join(', '),
          operation: balanceBonusOperation
        }),
        tips > 0 && incomeAPI.create({
          income_category_code: "tips",
          amount_cents: Math.round(tips * 100),
          date: date,
          reference: [date, t('bolt.income.tipsReference')].join(', '),
          operation: balanceTipsOperation
        }),
      ]

      trips.forEach(trip => {
        const tripReference = [
          trip.date,
          trip.reference
        ].join(', ')

        promises.push(
          incomeAPI.create({
            income_category_code: "trip",
            amount_cents: Math.round(trip.amount * 100),
            date: trip.date,
            reference: tripReference,
            operation: t('common.tripOperation')
          })
        )
      })

      /* Persist the analyzed monthly statement (importantly the distance_km)
         alongside the incomes */
      const lineItems = balance?.line_items || []

      if (balance?.reference?.date && lineItems.length > 0) {
        promises.push(
          monthlyStatementsAPI.create({
            source: 'bolt',
            date: balance.reference.date,
            distance_km: lineItems.find(item => item.category === 'distance_km')?.amount,
            line_items: lineItems
          })
        )
      }

      /* Duplicate references (re-uploading a month) are skipped by the API —
         the driver can delete the month and re-upload for a clean redo */
      await Promise.allSettled(promises.filter(Boolean) as Promise<unknown>[])

      resetAnalysis()
      setIsLoading(false)
      navigate("/income")
      Haptics.notification({ type: NotificationType.Success })
      requestInAppReview(dashboard)
    } else {
      /* This is available when User clicks "Add without Document" */

      const form = new FormData(e.currentTarget)
      const date = form.get('date') as string
      const reference = form.get('reference') as string

      const promises = [
        trip > 0 && incomeAPI.create({
          income_category_code: "trip",
          amount_cents: Math.round(trip * 100),
          date: date,
          reference: `${reference} ${t('bolt.income.tripReference')}`,
          operation: t('common.tripOperation')
        }),
        bonus > 0 && incomeAPI.create({
          income_category_code: "bonus",
          amount_cents: Math.round(bonus * 100),
          date: date,
          reference: `${reference} ${t('bolt.income.bonusReference')}`,
          operation: t('common.bonusOperation')
        }),
        tips > 0 && incomeAPI.create({
          income_category_code: "tips",
          amount_cents: Math.round(tips * 100),
          date: date,
          reference: `${reference} ${t('bolt.income.tipsReference')}`,
          operation: t('common.tipsOperation')
        }),
      ]

      try {
        await Promise.all(promises)
        navigate("/income")
        Haptics.notification({ type: NotificationType.Success })
        requestInAppReview(dashboard)
      } catch {
        setErrors({ reference: [t('common.referenceExists')] })
        setIsLoading(false)
      }
    }
  }

  /* Need to add ability to validate the amount not to be zero
     Passing just min value mask assigns when saving not triggering form validation */
  return (
    <form onSubmit={handleSubmit} className="h-full bg-white flex flex-col">
      <HeaderBack>
        <div className="flex flex-row items-center w-[8.5rem] ml-[3.84rem]">
          <ProgressBar
            currentStep={progress}
            totalSteps={3}
          />
        </div>
      </HeaderBack>
      <div className="h-full flex flex-col gap-4 px-5 pb-8">
        <MotionText
          as="h2"
          className="font-semibold text-3xl text-zinc-900"
          delay={0.1}
        >
          {t('bolt.income.title')}
        </MotionText>
        <MotionDiv>
          <DecimalInput
            className="w-[100%]"
            inputMode='decimal'
            name='income'
            label={t('common.tripIncome')}
            max={9999}
            postfix="\€"
            onChange={value => setTrip(parseFloat(value))}
            defaultValue={tripTotal}
            required
            disabled={trips?.length > 0}
          />
        </MotionDiv>
        <MotionDiv className="flex flex-row gap-4">
          <DecimalInput
            className="w-[100%]"
            inputMode='decimal'
            name='bonus'
            label={t('common.bonus')}
            max={9999}
            postfix="\€"
            onChange={value => setBonus(parseFloat(value))}
            defaultValue={balanceBonus}
            optional={true}
            optionalText={t('common.ifAny')}
          />
          <DecimalInput
            className="w-[100%]"
            inputMode='decimal'
            name='tips'
            label={t('common.tips')}
            max={9999}
            postfix="\€"
            onChange={value => setTips(parseFloat(value))}
            defaultValue={balanceTips}
            optional={true}
            optionalText={t('common.ifAny')}
          />
        </MotionDiv>
        {
          !trips?.length && <>
            <MotionDiv>
              <DateInput
                inputMode="numeric"
                className="w-[100%]"
                name='date'
                label={t('common.incomeDate')}
                placeholder={new Date().toISOString().split('T')[0]}
                required
              />
            </MotionDiv>
            <MotionDiv className="pb-[7rem]">
              <Input
                name='reference'
                label={t('common.documentReference')}
                required
                error={errors?.reference?.[0]}
              />
            </MotionDiv>
          </>
        }
        <div className="absolute bottom-0 -ml-5 px-5 pb-8 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%">
          <Button type="submit" isLoading={isLoading}>
            <span>{t('common.save')}</span>
          </Button>
        </div>
      </div>
    </form>
  )
}

export default BoltIncomePage